import React, { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/auth/useAuth';
import AuthModal, { AuthMode } from './AuthModal';

// Context interface
interface AuthModalContextType {
  // Modal state
  isOpen: boolean;
  currentMode: AuthMode;
  redirectAfterAuth?: string;
  contextMessage?: string;
  
  // Actions
  openModal: (mode?: AuthMode, options?: { redirectTo?: string; contextMessage?: string }) => void;
  closeModal: () => void;
  switchMode: (mode: AuthMode) => void;
  setRedirectAfterAuth: (url?: string) => void;
  resetPreventClose: () => void;
  
  // Legacy compatibility methods
  showLoginBox: boolean;
  showSignUpBox: boolean;
  showPasswordBox: boolean;
  showEmailBox: boolean;
  closeAllPopups: () => void;
  reloadPageAndClosePopups: () => void;
  handleLoginButtonClick: () => void;
  showSignUp: () => void;
  showPassword: () => void;
  showEmail: () => void;
}

// Create context with default values
export const AuthModalContext = createContext<AuthModalContextType>({
  isOpen: false,
  currentMode: 'login',
  redirectAfterAuth: undefined,
  contextMessage: undefined,
  openModal: () => {},
  closeModal: () => {},
  switchMode: () => {},
  setRedirectAfterAuth: () => {},
  resetPreventClose: () => {},
  
  // Legacy compatibility
  showLoginBox: false,
  showSignUpBox: false,
  showPasswordBox: false,
  showEmailBox: false,
  closeAllPopups: () => {},
  reloadPageAndClosePopups: () => {},
  handleLoginButtonClick: () => {},
  showSignUp: () => {},
  showPassword: () => {},
  showEmail: () => {},
});

interface AuthModalProviderProps {
  children: ReactNode;
}

export const AuthModalProvider: React.FC<AuthModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMode, setCurrentMode] = useState<AuthMode>('login');
  const [redirectAfterAuth, setRedirectAfterAuth] = useState<string | undefined>(undefined);
  const [contextMessage, setContextMessage] = useState<string | undefined>(undefined);
  const [preventClose, setPreventClose] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isLoggedIn, isEmailVerified, isCheckingAuth } = useAuth();

  // Modal actions
  const openModal = (mode: AuthMode = 'login', options?: { redirectTo?: string; contextMessage?: string }) => {
    setCurrentMode(mode);
    setIsOpen(true);
    if (options?.redirectTo) {
      setRedirectAfterAuth(options.redirectTo);
    }
    if (options?.contextMessage) {
      setContextMessage(options.contextMessage);
    }
  };

  const closeModal = () => {
    // Don't allow closing if in verification mode and preventClose is true
    if (preventClose && currentMode === 'verification') {
      return;
    }
    setIsOpen(false);
    setCurrentMode('login'); // Reset to login mode for next time
    setRedirectAfterAuth(undefined); // Clear redirect URL
    setContextMessage(undefined); // Clear context message
    setPreventClose(false); // Reset prevent close
  };

  const switchMode = (mode: AuthMode) => {
    setCurrentMode(mode);
    // Enable preventClose when switching to verification mode
    if (mode === 'verification') {
      setPreventClose(true);
    }
  };

  // Function to reset preventClose (for verification completion)
  const resetPreventClose = () => {
    setPreventClose(false);
  };


  // Legacy compatibility methods
  const closeAllPopups = closeModal;
  
  const reloadPageAndClosePopups = () => {
    closeModal();
    window.location.reload();
  };

  const handleLoginButtonClick = () => openModal('login');
  const showSignUp = () => openModal('signup');
  const showPassword = () => openModal('password');
  const showEmail = () => openModal('email');

  // Legacy state getters for backwards compatibility
  const showLoginBox = isOpen && currentMode === 'login';
  const showSignUpBox = isOpen && currentMode === 'signup';
  const showPasswordBox = isOpen && currentMode === 'password';
  const showEmailBox = isOpen && currentMode === 'email';

  // Keyboard and click outside handling
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        // Don't close on Escape if in persistent verification mode
        if (preventClose && currentMode === 'verification') {
          return;
        }
        closeModal();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        modalRef.current &&
        !modalRef.current.contains(target) &&
        target.getAttribute('id') !== 'loginButtonNav' &&
        target.getAttribute('id') !== 'loginButtonNavDropdown'
      ) {
        // Don't close on backdrop click if in persistent verification mode
        if (preventClose && currentMode === 'verification') {
          return;
        }
        closeModal();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress);
      document.addEventListener('mousedown', handleClickOutside);
      
      // Prevent body scroll when modal is open
      // Calculate scrollbar width and compensate for it
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        // Also apply to fixed navbar if it exists
        const navbar = document.querySelector('nav');
        if (navbar) {
          (navbar as HTMLElement).style.paddingRight = `${scrollbarWidth}px`;
        }
      }
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('mousedown', handleClickOutside);
      
      // Restore body scroll and remove padding
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      // Also restore navbar padding
      const navbar = document.querySelector('nav');
      if (navbar) {
        (navbar as HTMLElement).style.paddingRight = '';
      }
    };
  }, [isOpen]);

  // Context value
  const contextValue: AuthModalContextType = {
    // New unified API
    isOpen,
    currentMode,
    redirectAfterAuth,
    contextMessage,
    openModal,
    closeModal,
    switchMode,
    setRedirectAfterAuth,
    resetPreventClose,
    
    // Legacy compatibility
    showLoginBox,
    showSignUpBox,
    showPasswordBox,
    showEmailBox,
    closeAllPopups,
    reloadPageAndClosePopups,
    handleLoginButtonClick,
    showSignUp,
    showPassword,
    showEmail,
  };

  return (
    <AuthModalContext.Provider value={contextValue}>
      <div ref={modalRef}>
        <AuthModal 
          isOpen={isOpen}
          onClose={closeModal}
          initialMode={currentMode}
          redirectAfterAuth={redirectAfterAuth}
          contextMessage={contextMessage}
          preventClose={preventClose}
          resetPreventClose={resetPreventClose}
        />
      </div>
      {children}
    </AuthModalContext.Provider>
  );
};

// Custom hook for easy context access
export const useAuthModal = () => {
  const context = React.useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};